import React, { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import PropTypes from 'prop-types';

/**
 * NarrativeSection
 * One "Why It Matters" narrative paragraph. Paragraphs with labeled bullet
 * `groups` (e.g. "The Data We Don't Have") tend to run long, so the groups
 * are collapsed behind a "Read more" toggle to keep the page scannable.
 */
function BulletGroup({ label, bullets }) {
    return (
        <div className="mb-2">
            <p className="fw-semibold mb-1">{label}</p>
            <ul className="section-description mb-0 ps-3">
                {bullets.map((bullet, bi) => (
                    <li key={bi}>{bullet}</li>
                ))}
            </ul>
        </div>
    );
}

BulletGroup.propTypes = {
    label: PropTypes.string.isRequired,
    bullets: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default function NarrativeSection({ para }) {
    const [expanded, setExpanded] = useState(false);
    const groups = para.groups || [];
    const [firstGroup, ...restGroups] = groups;
    const hasMoreGroups = restGroups.length > 0;
    const collapseId = hasMoreGroups
        ? `narrative-groups-${para.heading.replace(/\s+/g, '-').toLowerCase()}`
        : undefined;

    return (
        <div className="mb-4">
            <h3 className="fs-5 fw-semibold mb-2">{para.heading}</h3>
            {para.text && (
                <p className="section-description mb-2">{para.text}</p>
            )}
            {para.bullets && (
                <ul className="section-description mb-2 ps-3">
                    {para.bullets.map((bullet, bi) => (
                        <li key={bi}>{bullet}</li>
                    ))}
                </ul>
            )}
            {firstGroup && (
                <BulletGroup
                    label={firstGroup.label}
                    bullets={firstGroup.bullets}
                />
            )}
            {hasMoreGroups && (
                <>
                    <Collapse in={expanded}>
                        <div id={collapseId}>
                            {restGroups.map((group, gi) => (
                                <BulletGroup
                                    key={gi}
                                    label={group.label}
                                    bullets={group.bullets}
                                />
                            ))}
                        </div>
                    </Collapse>
                    <button
                        type="button"
                        className="btn btn-link p-0 small fw-semibold mb-2 narrative-toggle"
                        onClick={() => setExpanded((prev) => !prev)}
                        aria-expanded={expanded}
                        aria-controls={collapseId}
                    >
                        {expanded ? 'Show less' : 'Read more'}
                        <i
                            className={`bi ${
                                expanded ? 'bi-chevron-up' : 'bi-chevron-down'
                            } ms-1`}
                            aria-hidden="true"
                        />
                    </button>
                </>
            )}
            <div className="d-flex flex-wrap gap-2">
                {para.sources.map((src, si) => (
                    <a
                        key={si}
                        href={src.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="why-source-link small"
                    >
                        {src.name} &#x2197;
                    </a>
                ))}
            </div>
        </div>
    );
}

NarrativeSection.propTypes = {
    para: PropTypes.shape({
        heading: PropTypes.string.isRequired,
        text: PropTypes.string,
        bullets: PropTypes.arrayOf(PropTypes.string),
        groups: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                bullets: PropTypes.arrayOf(PropTypes.string).isRequired,
            })
        ),
        sources: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string.isRequired,
                url: PropTypes.string.isRequired,
            })
        ).isRequired,
    }).isRequired,
};
